'use client'

import { Button as BaseButton } from '@base-ui/react/button'
import * as stylex from '@stylexjs/stylex'
import { cloneElement, useContext, type ComponentPropsWithRef, type ReactElement, type ReactNode } from 'react'

import { colors, controlSizes, fonts, fontSizes, lineHeights, radii, spacing } from '../tokens/tokens.stylex'
import { actionStyles } from '../action'
import { LinkRendererContext } from './link-provider'

const styles = stylex.create({
  control: { boxSizing: 'border-box', textDecoration: 'none' },
  primary: {
    alignSelf: 'flex-start',
    flexShrink: 0,
    backgroundColor: colors.action,
    borderWidth: 0,
    color: colors.actionForeground,
    fontSize: fontSizes.label,
    lineHeight: lineHeights.small,
    filter: { default: 'none', ':hover': 'brightness(0.95)', ':active': 'brightness(0.9)' },
    outlineOffset: 2,
    whiteSpace: 'nowrap',
  },
  primarySize: { minHeight: controlSizes.touch, paddingBlock: spacing.space12, paddingInline: spacing.space24 },
  secondary: {
    backgroundColor: { default: colors.background, ':hover': colors.surfaceAlt },
    borderColor: colors.borderStrong,
    color: colors.foreground,
  },
  quiet: {
    backgroundColor: { default: 'transparent', ':hover': colors.surfaceAlt },
    borderColor: 'transparent',
    color: colors.foreground,
  },
  danger: { backgroundColor: colors.dangerSurface, borderColor: colors.danger, color: colors.danger },
  tab: {
    backgroundColor: {
      default: 'transparent',
      ':hover': colors.hover,
      ':is([aria-current="page"])': colors.surfaceAlt,
    },
    borderColor: 'transparent',
    borderRadius: radii.medium,
    color: { default: colors.muted, ':hover': colors.foreground, ':is([aria-current="page"])': colors.foreground },
    fontFamily: fonts.sans,
    fontWeight: 500,
    outlineOffset: 2,
    whiteSpace: 'nowrap',
  },
  link: {
    color: colors.foreground,
    textDecoration: 'underline',
    textUnderlineOffset: 3,
    textDecorationColor: { default: colors.borderStrong, ':hover': colors.foreground },
    outlineColor: colors.foreground,
    outlineOffset: 3,
  },
  ringLinkFocus: { outline: { default: 'none', ':focus-visible': `2px solid ${colors.foreground}` } },
  hoverUnderline: { textDecoration: { default: 'none', ':hover': 'underline', ':focus-visible': 'underline' } },
})

type ButtonVariant = 'primary' | 'secondary' | 'quiet' | 'danger'
type ButtonSize = 'sm' | 'md'

type ButtonOptions = {
  focusableWhenDisabled?: boolean
  size?: ButtonSize
  variant?: ButtonVariant | undefined
}

export type ButtonProps = Omit<ComponentPropsWithRef<'button'>, 'className' | 'style' | 'color'> & ButtonOptions

/**
 * Use for an action; choose ButtonLink when activation navigates. Applications supply the action and own pending
 * and error state. Buttons default to type="button"; form submission requires type="submit". Use
 * focusableWhenDisabled to preserve keyboard focus while blocking a pending action. Status: supported.
 */
export function Button({ size = 'md', variant = 'secondary', type = 'button', ...props }: ButtonProps) {
  return (
    <BaseButton
      {...props}
      type={type}
      {...stylex.props(
        actionStyles.control,
        actionStyles[size],
        styles.control,
        styles[variant],
        variant === 'primary' && size === 'md' && styles.primarySize
      )}
    />
  )
}

type AnchorProps = Omit<ComponentPropsWithRef<'a'>, 'className' | 'style' | 'color'>

type LinkOptions = {
  children: ReactNode
  render?: ReactElement<AnchorProps>
  focus?: 'native' | 'ring'
  underline?: 'always' | 'hover'
}

export type LinkProps = AnchorProps & LinkOptions

/**
 * Use for a destination in prose; choose ButtonLink for prominent navigation. Applications supply the
 * destination and accessible text, and may provide a router-owned anchor through render. Use hover underlining
 * for record identities in a collection; prose keeps permanent underlining. Status: supported.
 */
export function Link({ render, children, focus = 'ring', underline = 'always', ...props }: LinkProps) {
  const renderLink = useContext(LinkRendererContext)

  const linkProps = {
    ...props,
    ...stylex.props(
      styles.link,
      focus === 'ring' && styles.ringLinkFocus,
      underline === 'hover' && styles.hoverUnderline
    ),
  }

  if (render) return cloneElement(render, linkProps, children)

  if (renderLink) return renderLink({ ...linkProps, children })

  return <a {...linkProps}>{children}</a>
}

type ButtonLinkOptions = {
  children: ReactNode
  render?: ReactElement<AnchorProps>
  size?: ButtonSize
  variant?: ButtonVariant | 'tab'
}

export type ButtonLinkProps = AnchorProps & ButtonLinkOptions

/**
 * Use for prominent navigation with button styling; choose Button for an action and Link within prose.
 * Applications supply the destination or router-owned anchor. The rendered element retains link navigation
 * semantics. Use the tab variant for peer page destinations with aria-current="page" on the current link. Status: supported.
 */
export function ButtonLink({ render, children, size = 'md', variant = 'secondary', ...props }: ButtonLinkProps) {
  const renderLink = useContext(LinkRendererContext)

  const linkProps = {
    ...props,
    ...stylex.props(
      actionStyles.control,
      actionStyles[size],
      styles.control,
      styles[variant],
      variant === 'primary' && size === 'md' && styles.primarySize
    ),
  }

  if (render) return cloneElement(render, linkProps, children)

  if (renderLink) return renderLink({ ...linkProps, children })

  return <a {...linkProps}>{children}</a>
}
